import { Link } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2, MapPin, Zap } from "lucide-react";
import { Button } from "./ui/button";
import { useAnalytics } from "@/hooks/useAnalytics";
import heroImage from "@/assets/hero-electrical.jpg";
import BrandBolt from "./BrandBolt";

const Hero = () => {
  const { trackPhoneClick, trackEmergencyClick } = useAnalytics();
  
  const benefits = [
    "Rychlé termíny",
    "Férové ceny",
    "Kvalitní práce",
  ];

  const services = ["Elektroinstalace", "Revize", "Servis", "Opravy", "Zabezpečovací systémy"];
  
  const locationTags = [
    { name: "Teplice", primary: true },
    { name: "Ústecký kraj", primary: false },
    { name: "do 50 km", primary: false },
  ];

  return (
    <header 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      role="banner"
      aria-label="Hlavní banner elektroinstalační firmy ESKO Servis"
    >
      {/* Background Image with Dark Overlay - Optimized for LCP */}
      <figure
        className="absolute inset-0"
        role="img"
        aria-label="Profesionální elektrikář při práci na elektroinstalaci v Teplicích"
      >
        <img 
          src={heroImage}
          alt="Profesionální elektrikář při práci na elektroinstalaci v Teplicích"
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
          decoding="async"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-amber-900/80" aria-hidden="true" />
        <figcaption className="sr-only">
          ESKO Servis - Profesionální elektrikářské služby v Teplicích a okolí
        </figcaption>
      </figure>

      {/* Decorative Elements - Hidden on mobile for performance */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block" aria-hidden="true">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-primary/10 rounded-full opacity-30" style={{ filter: 'blur(60px)' }} />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-primary/10 rounded-full opacity-25" style={{ filter: 'blur(60px)' }} />
        
        <div className="absolute top-1/2 left-1/2 hero-bolt-container">
          <svg
            viewBox="0 0 32 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-[600px] h-[900px] lg:w-[800px] lg:h-[1200px] hero-bolt"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="heroBoltGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFDD00" stopOpacity="0.12" />
                <stop offset="100%" stopColor="#FFCC00" stopOpacity="0.04" />
              </linearGradient>
            </defs>
            <path
              d="M20 0L6 22H14L10 48L28 18H18L20 0Z"
              fill="url(#heroBoltGradient)"
            />
          </svg>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10 pt-20 pb-24 md:pt-24 md:pb-32">
        <div className="max-w-4xl mx-auto text-center animate-fade-in">
          {/* Emergency Strip */}
          <Link 
            to="/pohotovost"
            className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-gradient-to-r from-red-600 to-red-500 text-white mb-5 hover:from-red-500 hover:to-orange-500 transition-all shadow-lg shadow-red-500/30 group"
            aria-label="Pohotovost 24/7 - nonstop elektrikářská služba, klikněte pro více informací"
            onClick={trackEmergencyClick}
          >
            <div className="flex items-center gap-1.5 sm:gap-2">
              <BrandBolt size="xs" variant="pulse" className="group-hover:scale-110" />
              <span className="font-bold text-xs sm:text-sm uppercase tracking-wide">Pohotovost 24/7</span>
            </div>
            <span className="hidden sm:inline text-white/80 text-sm" aria-hidden="true">|</span>
            <span className="hidden sm:flex items-center gap-1.5 text-sm">
              <Phone className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="font-semibold">+420 732 658 712</span>
            </span>
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
          </Link>

          {/* Main H1 - Shortened */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 leading-tight text-white">
            Váš <span className="text-primary">elektrikář</span> v Teplicích
          </h1>

          {/* Location Tags with microdata */}
          <div 
            className="flex flex-wrap justify-center gap-2 mb-6"
            itemScope
            itemType="https://schema.org/LocalBusiness"
          >
            <meta itemProp="name" content="ESKO Servis" />
            {locationTags.map((tag, index) => (
              <span 
                key={index}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  tag.primary 
                    ? 'bg-primary/20 text-primary border border-primary/30' 
                    : 'bg-white/10 text-white/80 border border-white/20'
                }`}
                itemProp="areaServed"
              >
                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                {tag.name}
              </span>
            ))}
          </div>

          {/* Service Card - Visual block */}
          <div className="relative max-w-xl mx-auto mb-6">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 sm:p-5">
              {/* Services row - responsive grid on mobile, inline on desktop */}
              <div className="flex flex-wrap justify-center items-center gap-x-1 gap-y-1.5 sm:gap-x-2 sm:gap-y-2 mb-3">
                <Zap className="h-5 w-5 text-primary hidden sm:block" aria-hidden="true" />
                {services.map((service, index) => (
                  <span key={index} className="flex items-center">
                    <span className="text-white font-semibold text-xs sm:text-sm md:text-base whitespace-nowrap">{service}</span>
                    {index < services.length - 1 && (
                      <span className="text-primary/60 mx-1 sm:mx-2 md:mx-3" aria-hidden="true">•</span>
                    )}
                  </span>
                ))}
              </div>
              {/* Tagline */}
              <p className="text-white/70 text-sm sm:text-base">
                Práce do 1000 V na klíč
              </p>
            </div>
          </div>

          {/* Benefits - Compact badges */}
          <ul className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 list-none" aria-label="Hlavní výhody služeb">
            {benefits.map((benefit, index) => (
              <li 
                key={index} 
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20"
              >
                <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                <span className="text-white/90 font-medium text-sm">{benefit}</span>
              </li>
            ))}
          </ul>

          {/* CTA Buttons */}
          <nav className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-10" aria-label="Hlavní akce">
            <Link to="/sluzby">
              <Button variant="electric" size="lg" className="gap-2 w-full sm:w-auto text-base px-6">
                Naše služby
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </Button>
            </Link>
            <a 
              href="tel:+420732658712" 
              aria-label="Zavolat ihned na číslo +420 732 658 712"
              onClick={() => trackPhoneClick('+420732658712')}
            >
              <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto border-white/30 bg-white/10 text-white hover:bg-white/20 hover:border-white/50">
                <Phone className="h-5 w-5" aria-hidden="true" />
                Zavolat ihned
              </Button>
            </a>
          </nav>

          {/* Phone Number Highlight */}
          <div className="mt-8">
            <a 
              href="tel:+420732658712" 
              className="inline-flex items-center gap-3 text-xl sm:text-2xl md:text-3xl font-bold text-primary hover:scale-105 transition-transform will-change-transform"
              aria-label="Telefonní číslo +420 732 658 712 - klikněte pro zavolání"
              onClick={() => trackPhoneClick('+420732658712')}
            >
              <div className="p-2 sm:p-3 rounded-full bg-primary/20" aria-hidden="true">
                <BrandBolt size="md" animate variant="glow" />
              </div>
              +420 732 658 712
            </a>
          </div>

          {/* Hidden SEO Content for crawlers */}
          <div className="sr-only">
            <p>
              Profesionální elektroinstalační, elektromontážní a demontážní práce do 1000 V. 
              Revize, servis, opravy a kompletní řešení na klíč. 
              Působíme v Teplicích, Ústí nad Labem, Mostě, Litoměřicích, Chomutově, Děčíně, Bílině a okolí do 50 km.
              Nonstop havarijní pohotovost 24/7. ESKO Servis - váš spolehlivý elektrikář v Ústeckém kraji.
            </p>
            <ul>
              <li>Elektroinstalace rodinných domů a bytů</li>
              <li>Revize elektrických zařízení</li>
              <li>Opravy a servis elektroinstalací</li>
              <li>Montáž fotovoltaických systémů</li>
              <li>Havarijní elektrikářská pohotovost</li>
            </ul>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Hero;
